import React, { useState, useEffect, useContext } from 'react';
import { getProjectDetails } from '../services/database';
import { getCustomPaletteById } from '../services/customPalettes';
import { Smartphone, Monitor } from 'lucide-react';
import palettes from '../data/palettes.json';
import fonts from '../data/fonts.json';
import { ProjectContext } from '../context/ProjectContext';

const StaticChoices = ({ projectId }) => {
  const { state, dispatch } = useContext(ProjectContext);
  const [isLoading, setIsLoading] = useState(true);
  const [customPalette, setCustomPalette] = useState(null);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        const projectDetails = await getProjectDetails(projectId);
        
        // Update context with all project details
        if (projectDetails) {
          dispatch({
            type: 'SET_PROJECT_DETAILS',
            payload: {
              title: projectDetails.title,
              styleChoices: projectDetails.styleChoices
            }
          });
          
          // If this is a custom palette, fetch its details only if we don't have them stored
          if (projectDetails.styleChoices?.isCustomPalette && 
              projectDetails.styleChoices?.customPaletteId &&
              !projectDetails.styleChoices?.customPaletteColors) {
            
            // If we don't have stored colors, try to fetch the palette
            const paletteData = await getCustomPaletteById(
              projectDetails.styleChoices.customPaletteId
            );
            setCustomPalette(paletteData);
          }
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching project details:', error);
        setIsLoading(false);
      }
    };

    fetchProjectDetails();
  }, [projectId, dispatch]);

  if (isLoading) {
    return <div className="flex items-center justify-center text-sm">Loading...</div>;
  }

  if (!state.styleChoices) {
    return <div className="flex items-center justify-center text-sm">No style choices found.</div>;
  }

  // Destructure style choices from context
  const { 
    device, 
    theme, 
    palette: paletteName, 
    font: fontName, 
    isCustomPalette, 
    customPaletteColors 
  } = state.styleChoices;

  // Choose device icon based on device type (Mobile or Desktop)
  const DeviceIcon = device && device.toLowerCase() === 'mobile' ? Smartphone : Monitor;

  // Find the font family from fonts.json
  const fontObj = fonts.find(f => f.name === fontName);
  const fontFamily = fontObj ? fontObj.family : 'sans-serif';

  // Get color values based on whether it's a custom or predefined palette
  let primaryColor, secondaryColor, accentColor;
  
  if (isCustomPalette) {
    // First try to use colors stored in the project (for deleted custom palettes)
    if (customPaletteColors?.hex) {
      primaryColor = customPaletteColors.hex.primary || '#000000';
      secondaryColor = customPaletteColors.hex.secondary || '#000000';
      accentColor = customPaletteColors.hex.accent || '#000000';
    }
    // If no stored colors, try to use fetched custom palette
    else if (customPalette) {
      primaryColor = customPalette.palette?.primary || '#000000';
      secondaryColor = customPalette.palette?.secondary || '#000000';
      accentColor = customPalette.palette?.accent || '#000000';
    }
    // Fallback to default colors if everything else fails
    else {
      primaryColor = '#000000';
      secondaryColor = '#666666';
      accentColor = '#333333';
    }
  } else {
    // Use predefined palette from palettes.json
    const availablePalettes = palettes[theme] || palettes['light']; // default to light if theme is invalid
    const paletteObj = availablePalettes.find(p => p.name === paletteName);

    // Fallback colors in case paletteObj is not found
    primaryColor = paletteObj ? paletteObj.primary : '#000000';
    secondaryColor = paletteObj ? paletteObj.secondary : '#000000';
    accentColor = paletteObj ? paletteObj.accent : '#000000';
  }

  // Define container styling based on theme
  const containerClasses = `flex items-center p-2 rounded border ${
    theme === 'light' ? 'bg-white text-black border-gray-200' : 'bg-gray-900 text-white border-transparent'
  }`;

  return (
    <div className={containerClasses}>
      <DeviceIcon className="w-5 h-5" />
      <span style={{ fontFamily: fontFamily }} className="mx-2 text-sm">
        {fontName}
      </span>
      <div className="flex space-x-2">
        <div className="w-4 h-4 rounded" style={{ backgroundColor: primaryColor }}></div>
        <div className="w-4 h-4 rounded" style={{ backgroundColor: secondaryColor }}></div>
        <div className="w-4 h-4 rounded" style={{ backgroundColor: accentColor }}></div>
      </div>
    </div>
  );
};

export default StaticChoices;